'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import { getPublishingRepository, type GalleryItem, type BasisItem } from '../repositories/PublishingRepository';

/**
 * Public (UNAUTHENTICATED) reads for the gallery + from-text basis picker.
 * These actions MUST NOT call SessionService — the gallery is reachable without
 * a session (proxy.ts allowlists /gallery). Only 'published' rows are exposed.
 */

export async function getPublicGallery(
  params: {
    limit?: number;
    offset?: number;
    tag?: string;
    kind?: 'audio' | 'video';
    /** One language, or several — a bilingual household filters on both. */
    language?: string;
    languages?: string[];
  } = {},
): Promise<GalleryItem[]> {
  try {
    return await getPublishingRepository().listPublished(params);
  } catch (e) {
    actionErrorLog('getPublicGallery', e);
    return [];
  }
}

/** Distinct languages across published stories — for the language filter. */
export async function getGalleryLanguages(): Promise<string[]> {
  try {
    return await getPublishingRepository().galleryLanguages();
  } catch (e) {
    actionErrorLog('getGalleryLanguages', e);
    return [];
  }
}

/** Public YouTube playlists for "watch continuously" — one per language ("*" = the rest). */
export async function getGalleryPlaylists(): Promise<Record<string, string>> {
  try {
    return await getPublishingRepository().getPlaylists();
  } catch (e) {
    actionErrorLog('getGalleryPlaylists', e);
    return {};
  }
}

export async function getPublicStory(storyId: string): Promise<GalleryItem | null> {
  try {
    return await getPublishingRepository().getPublishedById(storyId);
  } catch (e) {
    actionErrorLog('getPublicStory', e);
    return null;
  }
}

/** Reusable from-text bases (admin-approved, published). Used by the wizard picker.
 *  `language` (optional) narrows to bases in that language. */
export async function getReusableBasis(language?: string): Promise<BasisItem[]> {
  try {
    const lang = typeof language === 'string' && /^[a-z]{2}$/.test(language) ? language : undefined;
    return await getPublishingRepository().listReusableBasis(undefined, lang);
  } catch (e) {
    actionErrorLog('getReusableBasis', e);
    return [];
  }
}

export interface BasisPreview {
  id: string;
  title: string;
  text: string;
  characters: { name: string; description: string }[];
  source: 'admin' | 'story';
}

/**
 * Full text of ONE basis for the wizard's "read before you pick" panel. Both
 * kinds are public by construction (curated public-domain tales; users' stories
 * only when published + admin-marked reusable), so exposing the text is fine.
 */
export async function getBasisPreview(basisId: string): Promise<BasisPreview | null> {
  const id = String(basisId ?? '').trim();
  if (!id || id.length > 80) return null;
  try {
    const b = await getPublishingRepository().getBasisText(id);
    if (!b) return null;
    return { id, title: b.title, text: b.text, characters: b.characters, source: id.startsWith('bt_') ? 'admin' : 'story' };
  } catch (e) {
    actionErrorLog('getBasisPreview', e);
    return null;
  }
}
